/**
 * Training Repository Page - Browse all available trainings in a sortable table
 */

import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { learningApi } from '../api/learningApi'
import type { TrainingType, TrainingFilters } from '../types/learning.types'
import { useAuth } from '@/core/auth/AuthContext'

const TRAINING_TYPE_LABELS: Record<TrainingType, string> = {
  course: 'Course',
  workshop: 'Workshop',
  certification: 'Certification',
  webinar: 'Webinar',
  seminar: 'Seminar',
  conference: 'Conference',
  other: 'Other',
}

const TRAINING_TYPE_COLORS: Record<TrainingType, string> = {
  course: 'bg-red-100 text-red-700',
  workshop: 'bg-rose-100 text-rose-700',
  certification: 'bg-green-100 text-green-700',
  webinar: 'bg-orange-100 text-orange-700',
  seminar: 'bg-pink-100 text-pink-700',
  conference: 'bg-indigo-100 text-indigo-700',
  other: 'bg-gray-100 text-gray-700',
}

const DELIVERY_MODE_LABELS: Record<string, string> = {
  in_person: 'In Person',
  online: 'Online',
  hybrid: 'Hybrid',
}

type SortField =
  | 'name'
  | 'provider'
  | 'training_type'
  | 'delivery_mode'
  | 'duration_hours'
  | 'participant_count'
  | 'average_rating'
  | 'is_active'
  | 'created_at'
type SortOrder = 'asc' | 'desc'

const LIMIT = 25

const SortIcon = ({ active, order }: { active: boolean; order: SortOrder }) => {
  if (!active) {
    return (
      <svg className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    )
  }
  return order === 'asc' ? (
    <svg className="w-3.5 h-3.5 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-3.5 h-3.5 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

const TrainingRepositoryPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdminOrHR =
    user?.roles?.some((r) => r.name === 'admin' || r.name === 'hr') ||
    user?.division_name?.toLowerCase() === 'human resources'

  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedType, setSelectedType] = useState<TrainingType | ''>('')
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive'>('active')
  const [page, setPage] = useState(1)
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data: categoriesData } = useQuery({
    queryKey: ['trainingCategories'],
    queryFn: () => learningApi.getCategories(),
    staleTime: 10 * 60 * 1000,
  })

  const filters: TrainingFilters = useMemo(
    () => ({
      search: searchTerm || undefined,
      category_id: selectedCategory || undefined,
      training_type: selectedType || undefined,
      is_active: isAdminOrHR
        ? selectedStatus === 'all'
          ? undefined
          : selectedStatus === 'active'
        : true,
      page,
      limit: LIMIT,
      sort_by: sortField,
      sort_order: sortOrder,
    }),
    [searchTerm, selectedCategory, selectedType, selectedStatus, isAdminOrHR, page, sortField, sortOrder]
  )

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['trainings', filters],
    queryFn: () => learningApi.getTrainings(filters),
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000,
  })

  const trainings = data?.data || []
  const pagination = data?.pagination
  const categories = categoriesData?.data || []

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
    setPage(1)
  }

  const hasActiveFilters =
    searchInput || selectedCategory || selectedType || selectedStatus !== 'active'

  const ThSortable = ({
    field,
    children,
    className = '',
  }: {
    field: SortField
    children: React.ReactNode
    className?: string
  }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 select-none transition-colors ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1.5">
        {children}
        <SortIcon active={sortField === field} order={sortOrder} />
      </div>
    </th>
  )

  const colCount = isAdminOrHR ? 10 : 9

  const pageNumbers = (() => {
    if (!pagination || pagination.pages <= 1) return []
    const total = pagination.pages
    const delta = 2
    const left = Math.max(1, page - delta)
    const right = Math.min(total, page + delta)
    const range: number[] = []
    for (let i = left; i <= right; i++) range.push(i)
    if (left > 2) range.unshift(-1, 1)
    else if (left === 2) range.unshift(1)
    if (right < total - 1) range.push(-2, total)
    else if (right === total - 1) range.push(total)
    return range
  })()

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-2">
            <Link to="/learning" className="hover:text-white transition-colors">
              Learning
            </Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">Training Repository</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Training Repository</h1>
              <p className="text-red-200">Browse available trainings, courses, and certifications</p>
            </div>
            <div className="flex items-center gap-3">
              {isAdminOrHR && (
                <Link
                  to="/learning/trainings/new"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white font-medium rounded-xl hover:bg-red-400 transition-all shadow-lg border border-red-400"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Training
                </Link>
              )}
              <Link
                to="/learning/my-records"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 font-medium rounded-xl hover:bg-red-50 transition-all shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                My Training Records
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-6 pb-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by name, provider, or description..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-12 pr-10 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-red-500 focus:bg-white transition-all text-gray-900 placeholder-gray-400 text-sm"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value)
                setPage(1)
              }}
              className="px-4 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-red-500 text-gray-900 text-sm"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>

            <select
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value as TrainingType | '')
                setPage(1)
              }}
              className="px-4 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-red-500 text-gray-900 text-sm"
            >
              <option value="">All Types</option>
              {Object.entries(TRAINING_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            {isAdminOrHR && (
              <select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value as 'all' | 'active' | 'inactive')
                  setPage(1)
                }}
                className="px-4 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-red-500 text-gray-900 text-sm"
              >
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
                <option value="all">All Status</option>
              </select>
            )}

            {hasActiveFilters && (
              <button
                onClick={() => {
                  setSearchInput('')
                  setSelectedCategory('')
                  setSelectedType('')
                  setSelectedStatus('active')
                  setPage(1)
                }}
                className="px-4 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
          <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {isLoading ? (
                <span className="inline-block w-32 h-4 bg-gray-100 rounded animate-pulse" />
              ) : pagination ? (
                <>
                  Showing{' '}
                  <span className="font-semibold text-gray-900">
                    {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, pagination.total)}
                  </span>{' '}
                  of{' '}
                  <span className="font-semibold text-gray-900">{pagination.total}</span> trainings
                  {isFetching && !isLoading && (
                    <span className="ml-2 text-xs text-gray-400 animate-pulse">Updating…</span>
                  )}
                </>
              ) : (
                `${trainings.length} trainings`
              )}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <ThSortable field="name" className="pl-6 min-w-[220px]">
                    Name
                  </ThSortable>
                  <ThSortable field="provider" className="min-w-[140px]">
                    Provider
                  </ThSortable>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[120px]">
                    Category
                  </th>
                  <ThSortable field="training_type" className="min-w-[120px]">
                    Type
                  </ThSortable>
                  <ThSortable field="delivery_mode" className="min-w-[110px]">
                    Mode
                  </ThSortable>
                  <ThSortable field="duration_hours" className="min-w-[100px]">
                    Duration
                  </ThSortable>
                  <ThSortable field="participant_count" className="min-w-[110px]">
                    Participants
                  </ThSortable>
                  <ThSortable field="average_rating" className="min-w-[90px]">
                    Rating
                  </ThSortable>
                  {isAdminOrHR && (
                    <ThSortable field="is_active" className="min-w-[90px]">
                      Status
                    </ThSortable>
                  )}
                  <th className="px-4 py-3 pr-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[70px]">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-3.5">
                        <div className="h-4 bg-gray-100 rounded w-44" />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="h-4 bg-gray-100 rounded w-28" />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="h-4 bg-gray-100 rounded w-20" />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="h-5 bg-gray-100 rounded-full w-20" />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="h-4 bg-gray-100 rounded w-16" />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="h-4 bg-gray-100 rounded w-12" />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="h-4 bg-gray-100 rounded w-8" />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="h-4 bg-gray-100 rounded w-14" />
                      </td>
                      {isAdminOrHR && (
                        <td className="px-4 py-3.5">
                          <div className="h-5 bg-gray-100 rounded-full w-14" />
                        </td>
                      )}
                      <td className="px-4 py-3.5 pr-6">
                        <div className="h-4 bg-gray-100 rounded w-8" />
                      </td>
                    </tr>
                  ))
                ) : trainings.length === 0 ? (
                  <tr>
                    <td colSpan={colCount} className="px-6 py-16 text-center">
                      <svg
                        className="w-12 h-12 mx-auto text-gray-300 mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                      <p className="text-gray-500 font-medium">No trainings found</p>
                      <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
                    </td>
                  </tr>
                ) : (
                  trainings.map((training) => (
                    <tr
                      key={training.id}
                      className="hover:bg-red-50/40 cursor-pointer transition-colors"
                      onClick={() => navigate(`/learning/trainings/${training.id}`)}
                    >
                      <td className="px-6 py-3.5">
                        <span className="font-medium text-gray-900 text-sm line-clamp-1">
                          {training.name}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 max-w-[160px]">
                        <span className="line-clamp-1">
                          {training.provider || <span className="text-gray-300">—</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600">
                        {training.category?.name || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3.5">
                        <span
                          className={`px-2.5 py-1 text-xs font-medium rounded-full ${TRAINING_TYPE_COLORS[training.training_type]}`}
                        >
                          {TRAINING_TYPE_LABELS[training.training_type]}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600">
                        {training.delivery_mode ? (
                          DELIVERY_MODE_LABELS[training.delivery_mode] ?? training.delivery_mode
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600">
                        {training.duration_hours ? (
                          `${training.duration_hours}h`
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600">
                        {training.participant_count}
                      </td>
                      <td className="px-4 py-3.5">
                        {training.average_rating ? (
                          <div className="flex items-center gap-1">
                            <svg
                              className="w-3.5 h-3.5 text-yellow-400 shrink-0"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">
                              {training.average_rating.toFixed(1)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-sm">—</span>
                        )}
                      </td>
                      {isAdminOrHR && (
                        <td className="px-4 py-3.5">
                          <span
                            className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                              training.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            {training.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-3.5 pr-6">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/learning/trainings/${training.id}`)
                          }}
                          className="text-red-600 hover:text-red-800 text-sm font-medium transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              Page <span className="font-semibold text-gray-900">{page}</span> of{' '}
              <span className="font-semibold text-gray-900">{pagination.pages}</span> ·{' '}
              {pagination.total} total records
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="First page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 1}
                className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>

              {pageNumbers.map((n, i) =>
                n < 0 ? (
                  <span key={`ellipsis-${i}`} className="px-1 text-gray-400">
                    …
                  </span>
                ) : (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                      n === page
                        ? 'bg-red-600 text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {n}
                  </button>
                )
              )}

              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
              <button
                onClick={() => setPage(pagination.pages)}
                disabled={page >= pagination.pages}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Last page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 5l7 7-7 7M5 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TrainingRepositoryPage
